import type { ApiClientError } from '@/lib/api';
import {
  resolveMutationError,
  type MutationErrorDescriptor,
} from '@/src/client/error-handling/resolve-mutation-error';

/**
 * Translation function type for next-intl
 */
export type TranslationFunction = (
  key: string,
  values?: Record<string, string | number>
) => string;

/**
 * Options for translateMutationError
 */
export interface TranslateMutationErrorOptions {
  /**
   * Default translation key to use when error code is not recognized
   * @default 'errors.generic.unexpectedError'
   */
  defaultKey?: string;

  /**
   * Whether to return the raw translation key if translation fails
   * @default false - returns error.message or fallback string instead
   */
  returnKeyOnFailure?: boolean;
}

/**
 * Result of translateMutationError with diagnostic information
 */
export interface TranslateMutationErrorResult {
  /**
   * The translated user-friendly error message
   */
  message: string;

  /**
   * The error code from ApiClientError (if available)
   */
  code?: string;

  /**
   * HTTP status code (if available)
   */
  status?: number;

  /**
   * The translation descriptor used
   */
  descriptor: MutationErrorDescriptor;

  /**
   * Whether translation succeeded
   */
  translationSucceeded: boolean;
}

/**
 * Translates ApiClientError into a user-friendly display string.
 *
 * This is a unified utility that wraps resolveMutationError and handles
 * translation logic, providing consistent error message resolution across
 * the application.
 *
 * **Privacy-Safe:** Never includes sensitive error details (stack traces,
 * internal errors) in the returned message. Only returns translated strings
 * or sanitized fallback messages.
 *
 * @param error - The error to translate (typically ApiClientError)
 * @param translate - Translation function from next-intl (useTranslations)
 * @param options - Optional configuration
 * @returns Translated error message string
 *
 * @example
 * ```tsx
 * import { useTranslations } from 'next-intl';
 * import { translateMutationError } from '@/src/client/error-handling/translate-mutation-error';
 *
 * function MyComponent() {
 *   const t = useTranslations('errors');
 *
 *   const handleError = (error: unknown) => {
 *     const message = translateMutationError(error, t);
 *     toast.error(message);
 *   };
 * }
 * ```
 *
 * @example With custom default key
 * ```tsx
 * const message = translateMutationError(error, t, {
 *   defaultKey: 'errors.widget.operationFailed'
 * });
 * ```
 */
export function translateMutationError(
  error: unknown,
  translate: TranslationFunction,
  options: TranslateMutationErrorOptions = {}
): string {
  const { defaultKey, returnKeyOnFailure = false } = options;

  // Resolve error to translation descriptor
  const resolution = resolveMutationError(
    error,
    defaultKey ? { defaultKey } : {}
  );
  const { descriptor } = resolution;

  // Attempt translation
  try {
    const translatedMessage = translate(
      descriptor.key,
      descriptor.values as Record<string, string | number> | undefined
    );

    // Verify translation didn't just return the key (indicates missing translation)
    if (translatedMessage === descriptor.key) {
      // Translation key not found - fall back to error message or default
      if (returnKeyOnFailure) {
        return descriptor.key;
      }

      // Try to return a meaningful message
      if (resolution.message) {
        return resolution.message;
      }

      // Last resort fallback
      return 'An unexpected error occurred';
    }

    return translatedMessage;
  } catch (_translationError) {
    // Translation failed - return fallback
    if (returnKeyOnFailure) {
      return descriptor.key;
    }

    // Return the original error message if available
    if (resolution.message) {
      return resolution.message;
    }

    // Last resort fallback
    return 'An unexpected error occurred';
  }
}

/**
 * Extended version of translateMutationError that returns diagnostic information.
 *
 * Useful for debugging, testing, or when you need access to error metadata
 * in addition to the translated message.
 *
 * @param error - The error to translate
 * @param translate - Translation function from next-intl
 * @param options - Optional configuration
 * @returns Object with translated message and diagnostic information
 *
 * @example
 * ```tsx
 * const result = translateMutationErrorWithMetadata(error, t);
 * console.log('Code:', result.code);
 * console.log('Status:', result.status);
 * console.log('Message:', result.message);
 * console.log('Translation succeeded:', result.translationSucceeded);
 * ```
 */
export function translateMutationErrorWithMetadata(
  error: unknown,
  translate: TranslationFunction,
  options: TranslateMutationErrorOptions = {}
): TranslateMutationErrorResult {
  const { defaultKey, returnKeyOnFailure = false } = options;

  // Resolve error to translation descriptor
  const resolution = resolveMutationError(
    error,
    defaultKey ? { defaultKey } : {}
  );
  const { descriptor, code, status, message: errorMessage } = resolution;

  let translatedMessage: string;
  let translationSucceeded = false;

  // Attempt translation
  try {
    translatedMessage = translate(
      descriptor.key,
      descriptor.values as Record<string, string | number> | undefined
    );

    // Check if translation actually worked (key returned = missing translation)
    if (translatedMessage !== descriptor.key) {
      translationSucceeded = true;
    } else {
      // Translation key not found
      if (returnKeyOnFailure) {
        translatedMessage = descriptor.key;
      } else if (errorMessage) {
        translatedMessage = errorMessage;
      } else {
        translatedMessage = 'An unexpected error occurred';
      }
    }
  } catch (_translationError) {
    // Translation threw an error
    if (returnKeyOnFailure) {
      translatedMessage = descriptor.key;
    } else if (errorMessage) {
      translatedMessage = errorMessage;
    } else {
      translatedMessage = 'An unexpected error occurred';
    }
  }

  return {
    message: translatedMessage,
    ...(code && { code }),
    ...(status && { status }),
    descriptor,
    translationSucceeded,
  };
}

/**
 * Type guard to check if an error is an ApiClientError
 *
 * @param error - The error to check
 * @returns True if error is ApiClientError
 */
export function isApiClientError(error: unknown): error is ApiClientError {
  return (
    error instanceof Error &&
    'status' in error &&
    typeof (error as ApiClientError).status === 'number'
  );
}
